import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Badge, Banner, Button, EmptyState, PageBody, PageHeader } from "../ui/Field";

type SiteRow = { id: string; slug: string; name: string; role: string };

export default function Sites() {
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ sites: SiteRow[] }>("/api/auth/me")
      .then((r) => setSites(r.sites ?? []))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ Workspaces"
        title={<>All <em>sites</em></>}
        description="Every static site you have access to in this CMS deployment."
        actions={<Link to="/sites/new" className="inline-flex h-9 items-center px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13.5px] font-medium hover:bg-[var(--color-ink-2)] transition-colors no-underline">+ New site</Link>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      {loading && (
        <div className="grid gap-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-[var(--radius-sm)] bg-[var(--color-paper-2)] animate-pulse" />)}
        </div>
      )}

      {!loading && sites.length === 0 && (
        <EmptyState
          title="No workspaces yet."
          description="Sites are static-site projects — content, theme, assets, and a Pages deployment all bundled together."
          action={<Link to="/sites/new"><Button>Create your first site →</Button></Link>}
        />
      )}

      {!loading && sites.length > 0 && (
        <ol className="reveal reveal-2">
          {sites.map((s, i) => (
            <li key={s.id} className="border-b border-[var(--color-rule)] first:border-t group">
              <Link
                to={`/sites/${s.slug}`}
                className="block py-8 px-4 -mx-4 hover:bg-[var(--color-paper-2)] transition-colors no-underline group"
              >
                <div className="grid grid-cols-[40px_1fr_auto] gap-6 items-baseline">
                  <span className="font-mono text-[11px] text-[var(--color-muted)] tracking-[0.06em]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="display text-3xl md:text-4xl text-[var(--color-ink)] mb-1 group-hover:text-[var(--color-accent)] transition-colors">
                      {s.name}
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[12px] text-[var(--color-muted)]">/{s.slug}</span>
                      <span className="text-[11px] text-[var(--color-mute-2)]">·</span>
                      <Badge tone={s.role === "owner" || s.role === "superuser" ? "accent" : "neutral"}>{s.role}</Badge>
                    </div>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 18 18" className="text-[var(--color-mute-2)] group-hover:text-[var(--color-accent)] transition-colors">
                    <path d="M5 13L13 5M13 5H7M13 5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  </svg>
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </PageBody>
  );
}
